import React, { useState, useEffect, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import debounce from "lodash/debounce";
import DeleteIcon from "@mui/icons-material/Delete";
import { Close } from "@mui/icons-material";
//import EditIcon from "@mui/icons-material/Edit";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { useDeletePatient, useGetPatients } from "../../api/ekoApi";
import { useNavigate } from "react-router-dom";
import { AdminTabs } from "../../components/AdminTabs";
import { SearchInput } from "../../components/SearchInput";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { StepperHorizontal } from "../../components/forms/Stepper";

const TabAndSearchWrapper = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  padding-right: 10px;
  align-items: center;
`;

const AllPatientsList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { data, isLoading, isError, refetch } = useGetPatients(searchTerm);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(2);
  const [patientData, setPatientData] = useState(null);
  const [stepperModalOpen, setStepperModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const deletePatient = useDeletePatient();

  useEffect(() => {
    if (currentTab === 1) {
      navigate("/alluserstable");
    }
    if (currentTab === 2) {
      navigate("/allpatientstable");
    }
    if (currentTab === 0) {
      navigate("/admintable");
    }
  }, [currentTab, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((value, refetch) => {
      refetch({ searchTerm: value });
    }, 400),
    []
  );

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    handleSearch(value, refetch);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleStepperModalOpen = (data) => {
    setPatientData(data);
    setStepperModalOpen(true);
  };
  const handleStepperModalClose = () => {
    setStepperModalOpen(false);
  };

  const handleDelete = (rowData) => {
    setDeleteModalOpen(true);
    setSelectedRowData(rowData);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedRowData(null);
  };

  const handleCloseDeactivateModal = () => {
    setDeactivateModalOpen(false);
    setSelectedRowData(null);
  };

  const handleConfirmDelete = () => {
    deletePatient.mutate({ id: selectedRowData._id });
    handleCloseDeleteModal();
  };

  const handleConfirmDeactivate = () => {
    handleCloseDeactivateModal();
  };

  const handleNavigate = (data) => {
    navigate("/report", { state: { personalData: data } });
  };

  const columnDefsAdmins = [
    {
      field: "Name",
      headerName: t("name"),
      valueFormatter: ({ data }) => {
        const firstAndLastNAme = `${data.firstName} ${data.lastName}`;
        return firstAndLastNAme;
      },
      sortable: true
    },

    { field: "phoneNumber1", headerName: t("phoneNumber1"), width: 150, sortable: true },
    { field: "email", headerName: t("emailLabel"), width: 300, sortable: true },
    {
      field: "examinationdate",
      headerName: t("examinationDate"),
      valueFormatter: ({ value }) => {
        const date = new Date(value);
        const formattedDate = `${date.getDate()}/${
          date.getMonth() + 1
        }/${date.getFullYear()}`;
        return formattedDate;
      },
      sortable: true
    },
    {
      field: "birthdate",
      headerName: t("dateofBirth"),
      valueFormatter: ({ value }) => {
        const date = new Date(value);
        const formattedDate = `${date.getDate()}/${
          date.getMonth() + 1
        }/${date.getFullYear()}`;
        return formattedDate;
      },
      sortable: true
    },
    {
      field: "admittedBy.displayName",
      headerName: t("admittedBy"),
      width: 200,
      sortable: true
    },
    { field: "organization", headerName: t("organization"), width: 130, sortable: true },
    {
      field: "admittedBy.town",
      headerName: t("town"),
      width: 200,
      sortable: true
    },
    { field: "serialNumber", headerName: t("serialNumber"), sortable: true },
    {
      headerName: t("report"),
      cellRenderer: (params) => {
        return (
          <>
            <SummarizeOutlinedIcon
              onClick={() => handleNavigate(params.data)}
              style={{ cursor: "pointer" }}
            />
          </>
        );
      },

      
    },
    {
      headerName: t("actions"),
      cellRenderer: (params) => (
        <>
          <EditNoteIcon
            onClick={() => handleStepperModalOpen(params)}
            style={{ cursor: "pointer", color: "blue", marginRight: "10px" }}
          />
          <DeleteIcon
            onClick={() => handleDelete(params.data)}
            style={{ cursor: "pointer" }}
          />
        </>
      ),
    },
  ];

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error loading admin data: {isError.message}</div>;
  }

  const rowData = data?.patients || [];

  return (
    <div>
      <TabAndSearchWrapper>
        <AdminTabs currentTab={currentTab} handleTabChange={handleTabChange} />
        <SearchInput
          searchText={searchTerm}
          handleSearchInputChange={handleSearchInput}
        />
      </TabAndSearchWrapper>
      <div
        className="ag-theme-alpine"
        style={{ height: "80vh", width: "100%" }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefsAdmins}
          localeText={{ noRowsToShow: t("noRowsToShow") }}
        ></AgGridReact>
      </div>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>{t("deleteConfirmation")}</DialogTitle>
        <DialogContent>
          {t("areYouSureToDelete")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmDelete} color="error">
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={stepperModalOpen}
        onClose={handleStepperModalClose}
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            backgroundColor: "black",
            color: "white",
            paddingTop: 1,
            paddingBottom: 1,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {t("editPatient")}
          <IconButton onClick={handleStepperModalClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <StepperHorizontal patientData={patientData} edit />
        </DialogContent>
      </Dialog>
      {/* Deactivate Modal */}
      <Dialog open={deactivateModalOpen} onClose={handleCloseDeactivateModal}>
        <DialogTitle>{t("deactivateConfirmation")}</DialogTitle>
        <DialogContent>
          {t("areYouSureDeactivate")}{" "}
          {selectedRowData && selectedRowData.username}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeactivateModal}>{t("cancel")}</Button>
          <Button onClick={handleConfirmDeactivate}>{t("deaktivate")}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AllPatientsList;
